/**
 * SESSION WARNING MODAL
 * =====================
 * 
 * Shows when user's session is about to expire.
 * User can click "Stay Logged In" to extend or "Logout" to end session.
 */

import React, { useEffect, useRef } from "react";

const SessionWarningModal = ({ isOpen, timeLeft, onExtend, onLogout }) => {
  const extendButtonRef = useRef(null);

  // Focus extend button when modal opens
  useEffect(() => {
    if (isOpen && extendButtonRef.current) {
      extendButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Enter") {
        onExtend();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onExtend]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-warning-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onExtend}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2
          id="session-warning-title"
          className="text-xl font-semibold text-center text-gray-900 mb-2"
        >
          Session Expiring Soon
        </h2>

        {/* Message */}
        {/* <p className="text-center text-gray-600 mb-2">
          Your session will expire in
        </p>
        
        {/* Timer */}
        {/* <p className="text-center text-3xl font-bold text-red-600 mb-4">
          {timeLeft}
        </p> */} 

        <p className="text-center text-gray-500 text-sm mb-6">
          Click "Stay Logged In" or press Enter to continue your session.
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            ref={extendButtonRef}
            onClick={onExtend}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
          >
            Stay Logged In
          </button>
          <button
            onClick={() => onLogout("user_choice")}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionWarningModal;
